/* eslint-disable no-magic-numbers */
import { f7, useStore } from 'framework7-react';
import { Router } from 'framework7/types';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { ChargePrepaid } from './ChargePrepaid/ChargePrepaid';
import {
  ChargePrepaidBillInfo,
  getChargePrepaidBillInfo,
} from './ChargePrepaidHelper';
import { useLinkTypePlusChargePrepaid } from './useChargePrepaid';
import { customerInfoApi } from '@/api/customerApi';
import {
  verifyPaidyAuthorizationApi,
  capturePaidyApi,
  closePaidyApi,
} from '@/api/payment';
import { isApp } from '@/config/device';
import { AuthInfoState, PaymentDetail } from '@/config/initialState';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import { paymentTypeId } from '@/consts/payment';
import { getAndSetCustomerInfo } from '@/hooks/api/customer/useCustomerInfo';
import { useHouseprepaidApplication } from '@/hooks/api/prepaid/useHouseprepaidApplication';
import { usePaidy } from '@/hooks/usePaidy';
import { PaymentMethod, Prepaid } from '@/types/api/customerApi';
import { ChangePaymentInfo } from '@/types/payment';
import { storeDispatch } from '@/utils/store';
import { f7CustomBack } from '@/utils/utils';

interface ChargePrepaidProps {
  f7router: Router.Router;
  previousPage: string;
  redirectPage: string;
  status: string;
  handleChangeBalanceTotal: (charging: number) => void;
}

export const ChargePrepaidPage: FC<ChargePrepaidProps> = ({
  f7router,
  previousPage = '',
  redirectPage = '',
  status,
  handleChangeBalanceTotal,
}) => {
  const { m_customer_id } = useStore(store, 'authInfo') as AuthInfoState;
  // store
  const prepaidsInfo = useStore(store, 'getFirstAdvancePaymentsInfo');
  const billsInfo = useStore(store, 'getBillInfo');
  const displayPayment = useStore(
    store,
    'getPrepaidChargePayment',
  ) as PaymentDetail;
  const prepaid = useStore(store, 'getFirstAdvancePaymentsInfo') as
    | Required<Prepaid>
    | undefined;

  const housePrepaidParams = useMemo(
    () => ({ m_customer_id }),
    [m_customer_id],
  );
  const { data } = useHouseprepaidApplication(housePrepaidParams);

  const currentPlan = data?.houseprepaids
    ? (data?.houseprepaids).find(
        (plan) => plan.m_prepaid_plan_id === prepaid?.m_prepaid_plan_id,
      )
    : undefined;

  // state
  const [billInfo, setBillInfo] = useState<ChargePrepaidBillInfo>(
    getChargePrepaidBillInfo(billsInfo, prepaidsInfo),
  );
  const [amount, setAmount] = useState<number>(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const [balanceAfterCharging, setBalanceAfterCharging] = useState(
    prepaidsInfo?.balance_total ?? 0,
  );

  // const
  const maxChargeAmount = useMemo(
    () => currentPlan?.monthly_charge_max ?? 1990000,
    [currentPlan],
  );

  const monthlyChargeUnit = 100;
  const currentBalance = prepaidsInfo?.balance_total ?? 0;

  const isActive = (): boolean => {
    if (amount <= 0 || !displayPayment.paymentMethodId) return false;
    return true;
  };

  const handleAddChargeAmountClick = (value: number) => {
    setAmount((amount ?? 0) + value);
  };

  const handleSetPaymentInfo = useCallback(
    (paymentInfo: ChangePaymentInfo) => {
      billInfo &&
        setBillInfo({
          ...billInfo,
          bank_name: paymentInfo.bankName,
          account_number: paymentInfo.accountNumber,
          card_no: paymentInfo.cardNo,
          m_payment_type_id: paymentInfo.paymentTypeId,
        });
    },
    [billInfo],
  );
  const handleBack = () => {
    window.localStorage.removeItem('source_page');
    window.localStorage.removeItem('charge_amount');
    const postGmoNav = window.localStorage.getItem('postGmoNav');
    if (postGmoNav) {
      // GMOリダイレクト後の遷移: F7がDOM上の古い.page-previousを優先するため、
      // f7CustomBack()ではなくnavigate()で/prepaid/に遷移する。
      window.localStorage.removeItem('postGmoNav');
      storeDispatch('setMenu', 'menu', 'prepaid');
      f7.view.main.router.navigate('/prepaid/', {
        transition: 'push-reverse',
      });
      return;
    }
    if (previousPage == 'charge_prepaid') {
      f7.view.main.router.navigate(paths.usePrepaid, {
        props: {
          f7router: f7router,
          handleChangeBalanceTotal: handleChangeBalanceTotal,
        },
      });
    }
    f7CustomBack();
  };
  const { GMOLinkTypePlusParameters, generateGMOLinkTypePlusParameters } =
    useLinkTypePlusChargePrepaid(previousPage);
  const { launchPaidyCheckout } = usePaidy();

  const handleClickLickTypeChargeAction = async () => {
    setIsProcessing(true);
    if (!amount) {
      setIsProcessing(false);
      return;
    }

    if (amount % monthlyChargeUnit !== 0) {
      f7.dialog.alert(
        '',
        `${monthlyChargeUnit.toLocaleString()}円単位で金額を指定してください。`,
      );
      setIsProcessing(false);
      return;
    }

    if (
      displayPayment.paymentTypeId != paymentTypeId.CREDIT &&
      displayPayment.paymentTypeId != paymentTypeId.PAIDY
    ) {
      f7.dialog.alert(
        '',
        '決済はクレジットカードまたはあと払い（ペイディ）のみです。',
      );
      setIsProcessing(false);
      return;
    }

    // Paidy決済の場合はモーダルフロー
    if (displayPayment.paymentTypeId == paymentTypeId.PAIDY) {
      let paidyPaymentId: string | undefined;
      try {
        const result = await launchPaidyCheckout({
          mode: 'payment',
          amount,
          orderRef: `prepaid_${Date.now()}`,
        });
        if (result.success && result.paymentId) {
          paidyPaymentId = result.paymentId;
          const verifyRes = await verifyPaidyAuthorizationApi({
            payment_id: result.paymentId,
          });
          if (!verifyRes.data.success) {
            await closePaidyApi({ payment_id: result.paymentId });
            f7.dialog.alert(
              '',
              'ペイディでの決済に失敗しました。別のお支払い方法をお試しください。',
            );
            return;
          }
          // Capture + プリペイドチャージ（バックエンド内部で実行）
          const captureRes = await capturePaidyApi({
            payment_id: result.paymentId,
            amount,
            m_customer_id: m_customer_id,
            m_prepaid_id: prepaid?.id ?? 0,
          });
          if (!captureRes?.data?.success) {
            f7.dialog.alert(
              '',
              'ペイディでの決済に失敗しました。別のお支払い方法をお試しください。',
            );
            return;
          }

          // 顧客情報（残高含む）を再取得してstoreを更新
          const { data: refreshData } = await customerInfoApi({
            m_customer_id,
          });
          if (refreshData) {
            await Promise.all([
              storeDispatch(
                'setCustomerInfo',
                'customerInfo',
                refreshData.customer,
              ),
              storeDispatch(
                'setAdvancePaymentsInfo',
                'advancePaymentsInfo',
                refreshData.prepaids,
              ),
              storeDispatch(
                'setPaymentMethodsInfo',
                'paymentMethodsInfo',
                refreshData.payment_methods,
              ),
              storeDispatch(
                'setBillsInfo',
                'billsInfo',
                refreshData.bill_infomations,
              ),
            ]);
          }

          f7.dialog.alert('チャージが完了しました', '', () => {
            f7CustomBack();
          });
        } else {
          f7.dialog.alert('', 'ペイディ決済がキャンセルされました');
        }
      } catch (error) {
        if (paidyPaymentId) {
          await closePaidyApi({ payment_id: paidyPaymentId }).catch(() => {});
        }
        f7.dialog.alert('', 'エラーが発生しました');
      } finally {
        setIsProcessing(false);
      }
      return;
    }

    // クレジットカード決済（GMOリダイレクト）
    try {
      const response = await generateGMOLinkTypePlusParameters(amount);
      if (!response) {
        f7.dialog.alert('', `エラーが発生しました`);
        return;
      }
      if (response.error || !response.success || !response.data.LinkUrl) {
        f7.dialog.alert('', `エラーが発生しました`);
      } else {
        const GMOLinkTypePlusUrl = response.data.LinkUrl;
        if (isApp) {
          location.href = GMOLinkTypePlusUrl;
        } else {
          window.location.href = GMOLinkTypePlusUrl;
        }
      }
    } catch (error) {
      f7.dialog.alert('', `エラーが発生しました`);
    } finally {
      setIsProcessing(false);
    }
  };

  //payment method
  const paymentMethodsInfo = useStore(
    store,
    'getPaymentMethodsInfo',
  ) as Required<PaymentMethod>[];
  let paymentList: PaymentDetail[] = [];

  // 登録済み決済方法（Paidy以外）をリストに追加
  paymentMethodsInfo
    .filter((item) => item.m_payment_type_id !== paymentTypeId.PAIDY)
    .map((item) => {
      paymentList.push({
        accountNumber: item.account_number,
        bankName: item.bank_name,
        cardNo: item.card_no,
        isSelected: false,
        noPayFlg: 0,
        paySubject: '',
        paymentMethodId: item.m_payment_method_id,
        paymentTypeId: item.m_payment_type_id,
        paymentTypeName: item.payment_type_name,
        warrantyNo: item.warranty_no,
        register: item.register,
      });
    });

  // Paidyを固定選択肢として常時追加（トークン登録不要）
  paymentList.push({
    accountNumber: '',
    bankName: '',
    cardNo: 'あと払い（ペイディ）',
    isSelected: false,
    noPayFlg: 0,
    paySubject: '',
    paymentMethodId: -1,
    paymentTypeId: paymentTypeId.PAIDY,
    paymentTypeName: 'あと払い（ペイディ）',
    warrantyNo: '',
    register: '',
  });

  const handleClickChangePaymentMethod = async (item: PaymentDetail) => {
    if (displayPayment?.paymentMethodId == item?.paymentMethodId) return;
    await storeDispatch(
      'setPrepaidChargePayment',
      'prepaidChargePayment',
      item,
    );
    handleSetPaymentInfo({
      bankName: item.bankName,
      accountNumber: item.accountNumber,
      cardNo: item.cardNo,
      paymentTypeId: item.paymentTypeId,
    });
  };
  const handleClickAddMethod = (canWithdrawFromBank?: boolean) => {
    window.localStorage.setItem('charge_amount', amount.toString());
    window.localStorage.setItem('prevPage', 'charge_prepaid');
    f7.view.main.router.navigate(paths.registerPaymentList, {
      props: {
        redirectPage: redirectPage ?? 'home',
        canWithdrawFromBank: canWithdrawFromBank ?? false,
      },
    });
  };

  // GMOリダイレクト後はSPAリロードでストアが初期化されmenu='home'になるため、
  // マウント時に即座にタブバーを'prepaid'に設定する。
  useEffect(() => {
    storeDispatch('setMenu', 'menu', 'prepaid');
  }, []);

  // GMOリダイレクト後にPrepaidSettingsPageから遷移してきた場合、
  // ストアデータがまだ空の可能性があるため自力で取得する
  useEffect(() => {
    if (!prepaidsInfo?.id && m_customer_id) {
      getAndSetCustomerInfo({ m_customer_id });
    }
  }, [prepaidsInfo, m_customer_id]);

  useEffect(() => {
    const chargePrice =
      Number(window.localStorage.getItem('charge_amount')) || 0;
    if (!!chargePrice) {
      setAmount(chargePrice);
      window.localStorage.removeItem('charge_amount');
    }
  }, []);

  useEffect(() => {
    if (displayPayment?.paymentMethodId || !paymentList?.length) {
      return;
    }
    const lastRegisteredPayment = paymentList.reduce((max, cur) => {
      const maxDate = new Date(max.register);
      const curDate = new Date(cur.register);
      return curDate > maxDate ? cur : max;
    }, paymentList[0]);

    handleClickChangePaymentMethod(lastRegisteredPayment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentMethodsInfo]);

  return (
    <ChargePrepaid
      amount={amount}
      changeAmount={setAmount}
      currentBalance={currentBalance}
      handleAddChargeAmountClick={handleAddChargeAmountClick}
      handleClickLickTypeChargeAction={handleClickLickTypeChargeAction}
      isActive={isActive}
      handleBack={handleBack}
      displayPayment={displayPayment}
      paymentList={paymentList}
      handleClickChangePaymentMethod={handleClickChangePaymentMethod}
      handleClickAddMethod={handleClickAddMethod}
      isProcessing={isProcessing}
    />
  );
};
